import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import searchFilterAccessorials from "@tKgpco/Handlers/Accessorials/SearchFilterAccessorials";
import setAccessorialSearchFilter from "@tKgpco/Handlers/Accessorials/SetAccessorialSearchFilter";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { AccessorialsFormValues } from "@tKgpco/Store/Data/Accessorials/AccessorialsState";
import searchFilterSecurityRoles from "@tKgpco/Store/Data/SecurityRoles/Handlers/SearchFilterRoles";
import setRoleFilterSearchFilter from "@tKgpco/Store/Data/SecurityRoles/Handlers/SetRoleFilterSearch";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    searchFilterSecurityRoles,
    setRoleFilterSearchFilter,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    selectFilter: state.KGPState.SecurityRolesStore.selectFilter,
    selectFilterText: state.KGPState.SecurityRolesStore.selectFilterText,
});

interface CustomWidgetStyles {
    wrapper?: InjectableCss;
    searchBox?: TextFieldPresentationProps;
}

const styles: CustomWidgetStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    searchBox: {
        cssOverrides: {
            formField: css`
                > div {
                    margin-top: 5px !important;
                }
                input {
                    padding: 0 33px 0 10px !important;
                }
            `,
        },
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RolesSearch: React.FC<Props> = ({
    id,
    searchFilterSecurityRoles,
    setRoleFilterSearchFilter,
    selectFilter,
    selectFilterText,
}) => {
    const doSearch = () => {
        if (selectFilterText === "") {
            searchFilterSecurityRoles({ page: 1 });
        } else if (selectFilter && selectFilterText) {
            searchFilterSecurityRoles({
                page: 1,
                [selectFilter as keyof AccessorialsFormValues]: selectFilterText,
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode !== 13) {
            return false;
        }
        doSearch();
    };

    useEffect(() => {
        return () => {
            setRoleFilterSearchFilter({ key: "selectFilter", value: "" });
            setRoleFilterSearchFilter({ key: "selectFilterText", value: "" });
        };
    }, []);

    useEffect(() => {
        doSearch();
    }, [selectFilter, selectFilterText]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                css={css`
                    width: 240px;
                    max-width: 450px;
                `}
            >
                <Select
                    value={selectFilter}
                    onChange={e => setRoleFilterSearchFilter({ key: "selectFilter", value: e.target.value })}
                >
                    <option disabled value="">
                        {translate("Select Column")}
                    </option>

                    <option value="roleName">{translate("Name")}</option>
                    <option value="customerNumber">{translate("Customer Enabled")}</option>
                    <option value="featureName">{translate("Page Enabled")}</option>
                    <option value="featureSubPage">{translate("Widget Enabled")}</option>
                    <option value="ootbPage">{translate("Pages Disabled")}</option>
                    <option value="ootbWidget">{translate("Widgets Disabled")}</option>
                </Select>
            </StyledWrapper>
            <TextField
                onChange={e => setRoleFilterSearchFilter({ key: "selectFilterText", value: e.target.value })}
                value={selectFilterText}
                placeholder={translate("Search")}
                iconClickableProps={{ onClick: doSearch }}
                iconProps={{ src: "Search", size: 16 }}
                onKeyDown={handleKeyDown}
                {...styles.searchBox}
            />
        </StyledWrapper>
    );
};

const RolesSearchWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RolesSearch),
    definition: {
        group: "Basic",
    },
};

export default RolesSearchWidget;
